import { inject, Injector } from '@angular/core';
import { Identifiable } from '@data-access/shared/types';
import {
  patchState,
  signalStoreFeature,
  withMethods,
  withProps,
} from '@ngrx/signals';

function isAllSelected<T extends Identifiable>(
  selectedRows: T[],
  allRows: T[],
): boolean {
  if (!allRows.length) return false;
  const selectedIds = new Set(selectedRows.map((r) => r.id));
  return allRows.every((r) => selectedIds.has(r.id));
}

export const withGridCheckBoxFeature = () =>
  signalStoreFeature(
    withProps(() => ({
      _checboxInjector: inject(Injector),
    })),
    withMethods(({ _checboxInjector, ...store }) => ({
      setAllRows: <T extends Identifiable>(selectedRows: T[], rows: T[]) => {
        const uniqueRows = rows.filter(
          (r) => !selectedRows.some((s) => s.id === r.id),
        );
        const combined = [...selectedRows, ...uniqueRows];
        patchState(store, {
          allRows: combined,
          isAllSelected: isAllSelected(selectedRows, combined),
          isIndeterminate:
            !isAllSelected(selectedRows, combined) && selectedRows.length > 0,
        });
      },

      applySelectAll: <T extends Identifiable>(
        selectedRows: T[],
        allRows: T[],
        checked: boolean,
      ) => {
        if (checked) {
          const selectedIds = new Set(selectedRows.map((r) => r.id));
          const toAdd = allRows.filter((r) => !selectedIds.has(r.id));
          const updated = [...selectedRows, ...toAdd];
          patchState(store, {
            selectedRows: updated,
            isAllSelected: true,
            isIndeterminate: false,
          });
        } else {
          const allIds = new Set(allRows.map((r) => r.id));
          const updated = selectedRows.filter((r) => !allIds.has(r.id));
          patchState(store, {
            selectedRows: updated,
            isAllSelected: false,
            isIndeterminate: false,
          });
        }
      },

      applySelectRow: <T extends Identifiable>(
        selectedRows: T[],
        allRows: T[],
        row: T,
        checked: boolean,
      ) => {
        const updated = checked
          ? [...selectedRows, row].filter(
              (currentRow, index, array) =>
                array.findIndex(
                  (existingRow) => existingRow.id === currentRow.id,
                ) === index,
            )
          : selectedRows.filter((selectedRow) => selectedRow.id !== row.id);

        patchState(store, {
          selectedRows: updated,
          isAllSelected: isAllSelected(updated, allRows),
          isIndeterminate:
            !isAllSelected(updated, allRows) && updated.length > 0,
        });
      },
    })),
  );
